import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, AlertCircle, Clock, Phone, Sparkles, 
  FileText, Users, ChevronRight, X 
} from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

/**
 * Setup Status Banner - Shows organization readiness state
 * 
 * States:
 * - 🔴 Action Required (channel not connected)
 * - 🟡 Setup In Progress (X of 4 complete)
 * - 🟢 Ready to Go (all required items complete)
 * 
 * Owner and Admin see banner. Operator does NOT.
 */
const SetupStatusBanner = ({ readiness, onDismiss, currentUserRole }) => {
  // Don't show for operators
  if (currentUserRole === 'operator') {
    return null;
  }

  // Don't show if readiness data not loaded
  if (!readiness) {
    return null;
  }

  const { 
    status, 
    completion_count, 
    total_required, 
    ready_for_live,
    channel_connected,
    ai_configured,
    human_in_loop_configured,
    intake_configured,
    quick_actions,
    warnings
  } = readiness;

  // Don't show if ready (optional: can show success message briefly)
  if (ready_for_live && (!warnings || warnings.length === 0)) {
    return null;
  }

  const progressPercent = (completion_count / total_required) * 100;

  const getStatusConfig = () => {
    if (!channel_connected) {
      return {
        icon: AlertCircle,
        color: 'border-red-200 bg-red-50 dark:bg-red-900/20',
        iconColor: 'text-red-500',
        title: 'Action Required',
        description: 'Connect your WhatsApp channel to start receiving messages'
      };
    }
    if (!ready_for_live) {
      return {
        icon: Clock,
        color: 'border-amber-200 bg-amber-50 dark:bg-amber-900/20',
        iconColor: 'text-amber-500',
        title: `Setup In Progress (${completion_count} of ${total_required} complete)`,
        description: 'Complete the remaining steps to go live'
      };
    }
    return {
      icon: CheckCircle2,
      color: 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20',
      iconColor: 'text-emerald-500',
      title: 'Ready to Go',
      description: 'Your system is configured and ready for live traffic'
    };
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const checklist = [
    { 
      id: 'channel', 
      label: 'WhatsApp Channel', 
      complete: channel_connected,
      path: '/dashboard/channels',
      icon: Phone
    },
    { 
      id: 'ai', 
      label: 'AI Configuration', 
      complete: ai_configured,
      path: '/dashboard/ai-setup',
      icon: Sparkles
    },
    { 
      id: 'hitl', 
      label: 'Human Approval Setting', 
      complete: human_in_loop_configured,
      path: '/dashboard/ai-setup',
      icon: Users
    }
  ];

  return (
    <Alert className={`mb-6 ${statusConfig.color}`} data-testid="setup-status-banner">
      <div className="flex items-start justify-between w-full">
        <div className="flex items-start gap-3 flex-1">
          <StatusIcon className={`h-5 w-5 mt-0.5 ${statusConfig.iconColor}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-sm">{statusConfig.title}</h4>
              {!ready_for_live && (
                <Badge variant="outline" className="text-xs">
                  {completion_count}/{total_required}
                </Badge>
              )}
            </div>
            <AlertDescription className="text-sm text-muted-foreground mb-3">
              {statusConfig.description}
            </AlertDescription>

            {/* Progress bar */}
            {!ready_for_live && (
              <div className="mb-3">
                <Progress value={progressPercent} className="h-2" />
              </div>
            )}

            {/* Checklist */}
            <div className="flex flex-wrap gap-3 mb-3">
              {checklist.map((item) => (
                <Link 
                  key={item.id} 
                  to={item.path}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors ${
                    item.complete 
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  {item.complete ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <item.icon className="h-3 w-3" />
                  )}
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Quick Actions */}
            {quick_actions && quick_actions.length > 0 && !ready_for_live && (
              <div className="flex flex-wrap gap-2">
                {quick_actions.slice(0, 2).map((action) => (
                  <Button
                    key={action.id}
                    variant="outline"
                    size="sm"
                    asChild
                    className="text-xs"
                    data-testid={`quick-action-${action.id}`}
                  >
                    <Link to={action.path}>
                      {action.label}
                      <ChevronRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                ))}
              </div>
            )}

            {/* Warnings */}
            {warnings && warnings.length > 0 && (
              <div className="mt-3 space-y-1">
                {warnings.map((warning, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400"
                  >
                    <AlertCircle className="h-3 w-3" />
                    {warning.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Dismiss button (only if ready) */}
        {ready_for_live && onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </Alert>
  );
};

export default SetupStatusBanner;
